import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import '../css/PlayerSearchDropdown.css';

const VIEWPORT_PADDING = 12;
const DROPDOWN_GAP = 6;
const MIN_DROPDOWN_WIDTH = 360;
const MAX_DROPDOWN_WIDTH = 440;
const MAX_DROPDOWN_HEIGHT = 460;

const normalizeStatus = player =>
  String(player?.memberStatus ?? player?.status ?? 'ACTIVE')
    .trim()
    .toUpperCase();

const PLAY_STYLE_LABELS = {
  FAST_POWER_MIX: 'ทั่วไป',
  TIGHT_MEDIUM_HEAVY: 'ขอเหนื่อย',
  ALL_ROUND_READY: 'ได้หมด พร้อมชน',
  ENJOY_FUN: 'เน้นฮาและเหนื่อย'
};

const getPlayStyleLabel = player =>
  PLAY_STYLE_LABELS[player?.daily_play_style] || null;

const getPartnerNames = player => {
  const partners = player?.partners;

  if (Array.isArray(partners)) {
    return partners
      .filter(Boolean)
      .map(item => (
        typeof item === 'object'
          ? String(item.name ?? item.partner_name ?? '').trim()
          : String(item).trim()
      ))
      .filter(Boolean);
  }

  if (typeof partners === 'string') {
    return partners
      .split(',')
      .map(item => item.trim())
      .filter(Boolean);
  }

  if (partners && typeof partners === 'object') {
    return Object.values(partners)
      .filter(Boolean)
      .map(item => (
        typeof item === 'object'
          ? String(item.name ?? item.partner_name ?? '').trim()
          : String(item).trim()
      ))
      .filter(Boolean);
  }

  return [];
};

const getInitials = name => {
  const characters = Array.from(String(name || '').trim());
  return characters.slice(0, 2).join('') || 'P';
};

const getHandLevel = player => {
  const rawRank = String(
    player?.level_rank ?? player?.rank ?? player?.hand_level ?? ''
  ).trim();
  const rankWithoutPrefix = rawRank.startsWith('มือ')
    ? rawRank.slice('มือ'.length).trim()
    : rawRank;
  const savedRank = rankWithoutPrefix.toUpperCase();

  if (savedRank === 'S-P') return 'S/P';
  if (savedRank === 'N-S' || savedRank === 'NP') return 'N/S';
  if (savedRank === 'NB PLUS' || savedRank === 'NBPLUS') return 'NB+';
  if (savedRank === 'N MINUS' || savedRank === 'NMINUS') return 'N-';
  if (savedRank === 'S-') return 'S';
  if (savedRank) return savedRank;

  const point = Number(player?.point || 0);
  if (point >= 700) return 'S/P';
  if (point >= 650) return 'S';
  if (point >= 600) return 'N/S';
  if (point >= 550) return 'N';
  if (point >= 500) return 'N-';
  if (point >= 450) return 'NB+';
  if (point >= 400) return 'NB';
  if (point >= 350) return 'BGM';
  if (point >= 300) return 'BG';
  return '-';
};

const parsePlayerTime = value => {
  if (!value) return Number.MAX_SAFE_INTEGER;
  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? Number.MAX_SAFE_INTEGER : timestamp;
};

const getWaitStartTime = player => parsePlayerTime(
  player?.last_finished_at ||
  player?.active_since ||
  player?.checkin_time ||
  player?.created_at
);

const getArrivalTime = player => parsePlayerTime(
  player?.checkin_time ||
  player?.active_since ||
  player?.created_at ||
  player?.last_finished_at
);

const comparePlayers = (left, right, sortMode) => {
  if (sortMode === 'ZERO_GAMES_WAIT') {
    const leftGames = Number(
      left?.totalMatches ?? left?.total_matches ?? left?.total_games ?? 0
    ) || 0;
    const rightGames = Number(
      right?.totalMatches ?? right?.total_matches ?? right?.total_games ?? 0
    ) || 0;

    if (leftGames !== rightGames) return leftGames - rightGames;

    const waitDiff = getWaitStartTime(left) - getWaitStartTime(right);
    if (waitDiff !== 0) return waitDiff;

    return String(left?.name || '').localeCompare(
      String(right?.name || ''),
      'th'
    );
  }

  const leftTime = sortMode === 'FIFO'
    ? getArrivalTime(left)
    : getWaitStartTime(left);
  const rightTime = sortMode === 'FIFO'
    ? getArrivalTime(right)
    : getWaitStartTime(right);

  if (leftTime !== rightTime) return leftTime - rightTime;
  return String(left?.name || '').localeCompare(
    String(right?.name || ''),
    'th'
  );
};

function PlayerSearchDropdown({
  value,
  allPlayers = [],
  usedIds = [],
  sortMode = 'WAIT_LONGEST',
  showSortOptions = false,
  onSortModeChange,
  onChange
}) {
  const listboxId = useId();
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [position, setPosition] = useState(null);
  const [now, setNow] = useState(Date.now());

  const wrapperRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setNow(Date.now());
    }, 60000);

    return () => window.clearInterval(intervalId);
  }, []);

  const activeSortMode = ['FIFO', 'ZERO_GAMES_WAIT'].includes(sortMode)
    ? sortMode
    : 'WAIT_LONGEST';
  const filtered = allPlayers
    .filter(player => {
      const isUsed = usedIds.some(
        usedId => Number(usedId) === Number(player.user_id)
      );

      if (isUsed) return false;
      if (!keyword.trim()) return true;

      const searchText = [
        player.name,
        getHandLevel(player),
        player.point,
        normalizeStatus(player),
        getPlayStyleLabel(player)
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchText.includes(keyword.trim().toLowerCase());
    })
    .sort((left, right) => comparePlayers(left, right, activeSortMode));

  const getIdleMinutes = player => {
    const waitStartedAt = getWaitStartTime(player);
    if (waitStartedAt === Number.MAX_SAFE_INTEGER) return null;

    return Math.max(0, Math.floor((now - waitStartedAt) / 60000));
  };

  useEffect(() => {
    if (!open) {
      setPosition(null);
      return undefined;
    }

    const updatePosition = () => {
      if (!wrapperRef.current) return;

      const rect = wrapperRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const availableWidth = Math.max(
        0,
        viewportWidth - VIEWPORT_PADDING * 2
      );
      const width = Math.min(
        MAX_DROPDOWN_WIDTH,
        availableWidth,
        Math.max(
          Math.min(MIN_DROPDOWN_WIDTH, availableWidth),
          rect.width
        )
      );
      const left = Math.min(
        Math.max(VIEWPORT_PADDING, rect.left),
        viewportWidth - width - VIEWPORT_PADDING
      );
      const roomBelow = viewportHeight - rect.bottom - VIEWPORT_PADDING;
      const roomAbove = rect.top - VIEWPORT_PADDING;
      const openAbove = roomBelow < 280 && roomAbove > roomBelow;
      const availableHeight = openAbove ? roomAbove : roomBelow;
      const maxHeight = Math.max(
        140,
        Math.min(MAX_DROPDOWN_HEIGHT, availableHeight - DROPDOWN_GAP)
      );
      const top = openAbove
        ? Math.max(
            VIEWPORT_PADDING,
            rect.top - maxHeight - DROPDOWN_GAP
          )
        : rect.bottom + DROPDOWN_GAP;

      setPosition({ top, left, width, maxHeight, openAbove });
    };

    updatePosition();

    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = event => {
      const clickedInput = wrapperRef.current?.contains(event.target);
      const clickedDropdown = dropdownRef.current?.contains(event.target);

      if (!clickedInput && !clickedDropdown) {
        setOpen(false);
        setKeyword('');
      }
    };

    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        setOpen(false);
        setKeyword('');
        wrapperRef.current?.querySelector('input')?.blur();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const closeDropdown = () => {
    setOpen(false);
    setKeyword('');
    wrapperRef.current?.querySelector('input')?.blur();
  };

  const selectPlayer = player => {
    onChange(player);
    closeDropdown();
  };

  return (
    <>
      <div ref={wrapperRef} className="player-search-wrapper">
        <span className="player-search-icon" aria-hidden="true">⌕</span>
        <input
          role="combobox"
          className="player-search-input"
          value={open ? keyword : value?.name || ''}
          placeholder="ค้นหาผู้เล่น"
          aria-controls={listboxId}
          aria-expanded={open}
          aria-haspopup="listbox"
          onFocus={() => {
            setKeyword('');
            setOpen(true);
          }}
          onChange={event => {
            setKeyword(event.target.value);
            setOpen(true);
          }}
        />
      </div>

      {open && position && typeof document !== 'undefined' &&
        createPortal(
          <div
            id={listboxId}
            ref={dropdownRef}
            className={`player-search-dropdown-portal ${
              position.openAbove ? 'opens-above' : 'opens-below'
            } ${showSortOptions ? 'has-sort-options' : ''}`}
            role="listbox"
            style={{
              top: position.top,
              left: position.left,
              width: position.width,
              maxHeight: position.maxHeight
            }}
          >
            <div className="player-search-dropdown-header">
              <div>
                <strong>เลือกผู้เล่น</strong>
                <span>{filtered.length} คน</span>
              </div>
              <button
                type="button"
                className="player-search-close-button"
                onClick={closeDropdown}
                aria-label="ปิดรายชื่อผู้เล่น"
              >
                ✕
              </button>
            </div>

            {showSortOptions && (
              <div className="player-search-sort-options">
                <span>เรียงรายชื่อ</span>
                <button
                  type="button"
                  className={activeSortMode === 'WAIT_LONGEST' ? 'active' : ''}
                  onClick={() => onSortModeChange?.('WAIT_LONGEST')}
                >
                  ⏱ รอนานที่สุด
                </button>
                <button
                  type="button"
                  className={activeSortMode === 'FIFO' ? 'active' : ''}
                  onClick={() => onSortModeChange?.('FIFO')}
                >
                  📥 มาก่อนที่สุด
                </button>
              </div>
            )}

            <div className="player-search-options">
              {filtered.length === 0 && (
                <div className="player-search-empty">
                  <span aria-hidden="true">🔎</span>
                  <strong>ไม่พบผู้เล่น</strong>
                  <small>ลองค้นหาด้วยชื่อ คะแนน หรือ Rank</small>
                </div>
              )}

              {filtered.map(player => {
                const idleMinutes = getIdleMinutes(player);
                const status = normalizeStatus(player);
                const handLevel = getHandLevel(player);
                const playStyleLabel = getPlayStyleLabel(player);
                const partnerNames = getPartnerNames(player);
                const gameCount = Number(
                  player.totalMatches ??
                  player.total_matches ??
                  player.total_games ??
                  0
                );
                const selected =
                  Number(value?.user_id) === Number(player.user_id);

                return (
                  <button
                    type="button"
                    key={player.user_id}
                    className={`dropdown-player ${selected ? 'selected' : ''}`}
                    role="option"
                    aria-selected={selected}
                    onClick={() => selectPlayer(player)}
                  >
                    <span className="dropdown-player-avatar">
                      {getInitials(player.name)}
                    </span>

                    <span className="dropdown-player-content">
                      <span className="dropdown-player-main">
                        <strong className="dropdown-player-name">
                          {player.name}
                        </strong>
                        <span className="dropdown-player-point">
                          {Number(player.point || 0).toLocaleString()} pts
                        </span>
                      </span>

                      <span className="dropdown-player-meta">
                        <span className="player-meta-chip rank-chip">
                          <span className="rank-chip-label">ระดับ</span>
                          {handLevel}
                        </span>
                        <span className="player-meta-chip game-chip">
                          🎮 {gameCount} เกม
                        </span>
                        {playStyleLabel && (
                          <span className="player-meta-chip play-style-chip">
                            🏸 {playStyleLabel}
                          </span>
                        )}
                        {status && (
                          <span
                            className={`player-meta-chip status-chip status-${status.toLowerCase()}`}
                          >
                            {status}
                          </span>
                        )}
                        <span
                          className={`player-meta-chip idle-chip ${
                            idleMinutes !== null && idleMinutes > 20
                              ? 'idle-very-high'
                              : idleMinutes !== null && idleMinutes > 10
                                ? 'idle-high'
                                : ''
                          }`}
                        >
                          ⏱ {idleMinutes === null ? '-' : `${idleMinutes} นาที`}
                        </span>
                      </span>

                      {partnerNames.length > 0 && (
                        <span className="dropdown-player-partners">
                          👥 คู่ล่าสุด: {partnerNames.join(', ')}
                        </span>
                      )}
                    </span>

                    <span className="dropdown-player-arrow" aria-hidden="true">
                      ›
                    </span>
                  </button>
                );
              })}
            </div>
          </div>,
          document.body
        )}
    </>
  );
}

export default PlayerSearchDropdown;
